<template>
  <div>
    <v-card>
      <v-card-title>
        <h3 class="headline">{{ $t('overview.project_stats') }}</h3>
        <v-spacer></v-spacer>
        <v-select
          v-model="selectedDays"
          :items="daysOptions"
          :label="$t('overview.time_period')"
          dense
          outlined
          style="max-width: 150px;"
          @change="loadStats"
        ></v-select>
      </v-card-title>
      <v-card-text class="pa-4">
        <div v-if="loading" class="text-center pa-8">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>
        <div v-else-if="stats.length === 0" class="text-center pa-8">
          <p class="text--secondary">{{ $t('overview.no_stats_available') }}</p>
        </div>
        <div v-else>
          <div class="chart-wrapper">
            <div class="y-axis-labels">
              <div
                v-for="(label, index) in yAxisLabels"
                :key="'y-label-' + index"
                class="y-axis-label"
              >
                {{ label }}
              </div>
            </div>
            <div class="sparkline-container">
              <svg
                :viewBox="`0 0 ${chartWidth} ${chartHeight}`"
                class="chart-svg"
                preserveAspectRatio="none"
              >
                <!-- Items Added line -->
                <polyline
                  :points="itemsAddedPath"
                  fill="none"
                  stroke="#1976d2"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                
                <!-- Assets Edited line -->
                <polyline
                  :points="assetsEditedPath"
                  fill="none"
                  stroke="#388e3c"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                
                <!-- Annotations Validated line -->
                <polyline
                  :points="annotationsValidatedPath"
                  fill="none"
                  stroke="#f57c00"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          </div>
          
          <!-- Legend -->
          <div class="mt-6 d-flex justify-center flex-wrap">
            <v-chip small color="#1976d2" text-color="white" class="mr-2 mb-2">
              {{ $t('overview.items_added') }}
            </v-chip>
            <v-chip small color="#388e3c" text-color="white" class="mr-2 mb-2">
              {{ $t('overview.assets_edited') }}
            </v-chip>
            <v-chip small color="#f57c00" text-color="white" class="mb-2">
              {{ $t('overview.annotations_validated') }}
            </v-chip>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'ProjectStatsGraph',
  data() {
    return {
      stats: [],
      loading: false,
      selectedDays: 30,
    };
  },
  computed: {
    daysOptions() {
      return [
        { text: this.$t('overview.days_7'), value: 7 },
        { text: this.$t('overview.days_30'), value: 30 },
        { text: this.$t('overview.days_90'), value: 90 },
        { text: this.$t('overview.days_180'), value: 180 },
      ];
    },
    displayedStats() {
      // Show max 30 points to avoid overcrowding
      if (this.stats.length <= 30) {
        return this.stats;
      }
      
      const indicesToInclude = new Set();
      
      // Always include first and last dates
      indicesToInclude.add(0);
      indicesToInclude.add(this.stats.length - 1);
      
      // Always include all indices with non-zero values
      this.stats.forEach((stat, index) => {
        if (stat.items_added > 0 || stat.assets_edited > 0 || stat.annotations_validated > 0) {
          indicesToInclude.add(index);
        }
      });
      
      // Fill in the rest evenly to reach ~30 points total
      const targetCount = 30;
      const remainingSlots = targetCount - indicesToInclude.size;
      
      if (remainingSlots > 0) {
        // Get all indices that aren't already included
        const availableIndices = [];
        for (let i = 0; i < this.stats.length; i++) {
          if (!indicesToInclude.has(i)) {
            availableIndices.push(i);
          }
        }
        
        // Sample evenly from available indices
        if (availableIndices.length > 0) {
          const step = Math.ceil(availableIndices.length / remainingSlots);
          for (let i = 0; i < availableIndices.length; i += step) {
            indicesToInclude.add(availableIndices[i]);
            if (indicesToInclude.size >= targetCount) break;
          }
        }
      }
      
      // Convert to sorted array and map to stats
      const sortedIndices = Array.from(indicesToInclude).sort((a, b) => a - b);
      return sortedIndices.map(index => this.stats[index]);
    },
    itemsAddedValues() {
      return this.displayedStats.map(s => s.items_added);
    },
    assetsEditedValues() {
      return this.displayedStats.map(s => s.assets_edited);
    },
    annotationsValidatedValues() {
      return this.displayedStats.map(s => s.annotations_validated);
    },
    maxValue() {
      if (this.displayedStats.length === 0) return 10;
      const allValues = [
        ...this.displayedStats.map(s => s.items_added),
        ...this.displayedStats.map(s => s.assets_edited),
        ...this.displayedStats.map(s => s.annotations_validated),
      ];
      const max = Math.max(...allValues);
      // Round up to nearest 10 for cleaner chart
      return Math.ceil(max / 10) * 10 || 10;
    },
    chartWidth() {
      // Use a reasonable default, will be scaled by CSS
      return 800;
    },
    chartHeight() {
      return 300;
    },
    chartPadding() {
      return 20;
    },
    // Calculate SVG path points for each line with proper scaling
    itemsAddedPath() {
      return this.calculatePath(this.itemsAddedValues);
    },
    assetsEditedPath() {
      return this.calculatePath(this.assetsEditedValues);
    },
    annotationsValidatedPath() {
      return this.calculatePath(this.annotationsValidatedValues);
    },
    yAxisLabels() {
      const labels = [];
      const steps = 5;
      for (let i = steps; i >= 0; i--) {
        labels.push(Math.round((this.maxValue * i) / steps));
      }
      return labels;
    },
  },
  mounted() {
    this.loadStats();
  },
  methods: {
    async loadStats() {
      this.loading = true;
      try {
        const response = await this.$api.overview.get_project_stats(this.selectedDays);
        // The API wrapper already returns response.data, so use response directly
        this.stats = response || [];
      } catch (error) {
        console.error('Error loading project stats:', error);
        this.stats = [];
      } finally {
        this.loading = false;
      }
    },
    calculatePath(values) {
      if (values.length === 0) return '';
      
      const width = this.chartWidth - (this.chartPadding * 2);
      const height = this.chartHeight - (this.chartPadding * 2);
      const maxVal = this.maxValue || 1;
      
      const points = values.map((value, index) => {
        // Handle single point case
        const xRatio = values.length === 1 ? 0.5 : index / (values.length - 1);
        const x = this.chartPadding + xRatio * width;
        // Y is inverted (0 at top, maxValue at bottom in SVG coordinates)
        const y = this.chartPadding + height - (value / maxVal) * height;
        return `${x},${y}`;
      });
      
      return points.join(' ');
    },
  },
};
</script>

<style scoped>
.chart-wrapper {
  display: flex;
  align-items: stretch;
  width: 100%;
}

.y-axis-labels {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-right: 10px;
  padding-top: 20px;
  padding-bottom: 20px;
  min-width: 40px;
  text-align: right;
  font-size: 12px;
  color: #666;
}

.y-axis-label {
  height: 0;
  line-height: 1;
}

.sparkline-container {
  position: relative;
  height: 300px;
  width: 100%;
  min-height: 300px;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-svg {
  width: 100%;
  height: 100%;
  max-width: 100%;
}
</style>

